(() => {
  if (window.ReusableSections) return;

  class MediaCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-media-carousel-track]');
      this.previous = root.querySelector('[data-media-carousel-previous]');
      this.next = root.querySelector('[data-media-carousel-next]');
      this.dots = [...root.querySelectorAll('[data-media-carousel-dot]')];
      this.originalSlides = [...(this.track?.querySelectorAll('.media-carousel__slide') || [])];
      this.slides = [...this.originalSlides];
      this.autoplay = root.dataset.autoplay === 'true';
      this.autoplayDelay = Number.parseInt(root.dataset.autoplayDelay, 10) || 6000;
      this.pauseOnHover = root.dataset.pauseOnHover === 'true';
      this.animationStyle = root.dataset.animationStyle || 'current';
      this.controlledSlides = root.dataset.controlledSlides === 'true';
      this.slidesVisible = Number.parseInt(root.dataset.slidesVisible, 10) || 1;
      this.reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
      this.activeIndex = 0;
      this.autoplayTimer = null;
      this.autoplayStartedAt = null;
      this.autoplayRemaining = this.autoplayDelay;
      this.animationFrame = null;
      this.navigationTimer = null;
      this.scrollEndTimer = null;
      this.resizeTimer = null;
      this.isHovered = false;
      this.hoveredSlide = null;
      this.hasFocus = false;
      this.isVisible = !('IntersectionObserver' in window);
      this.isNavigating = false;
      this.isNormalizing = false;
      this.infinite = false;
      this.drag = null;
      this.suppressClick = false;
    }

    init() {
      if (!this.track || this.root.dataset.mediaCarouselReady === 'true') return;
      this.root.dataset.mediaCarouselReady = 'true';
      this.setupInfiniteTrack();
      this.previous?.addEventListener('click', (event) => {
        this.move(-1);
        this.restartAutoplayAfterNavigation(event);
      });
      this.next?.addEventListener('click', (event) => {
        this.move(1);
        this.restartAutoplayAfterNavigation(event);
      });
      this.dots.forEach((dot, index) => dot.addEventListener('click', (event) => {
        this.goTo(index);
        this.restartAutoplayAfterNavigation(event);
      }));
      this.track.addEventListener('scroll', () => {
        this.update();
        this.scheduleScrollEnd();
      }, { passive: true });
      this.track.addEventListener('scrollend', () => this.finishNavigation());
      this.track.addEventListener('pointerdown', (event) => this.startDrag(event));
      this.track.addEventListener('pointermove', (event) => this.dragTrack(event));
      this.track.addEventListener('pointerup', (event) => this.endDrag(event));
      this.track.addEventListener('pointercancel', (event) => this.endDrag(event, true));
      this.track.addEventListener('dragstart', (event) => event.preventDefault());
      this.track.addEventListener('click', (event) => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, true);
      window.addEventListener('resize', () => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => {
          if (this.infinite) this.jumpToPhysicalIndex(this.originalSlides.length + this.activeIndex);
          this.update();
        }, 120);
      }, { passive: true });
      if (this.pauseOnHover) {
        this.slides.forEach((slide) => {
          slide.addEventListener('mouseenter', () => {
            const logicalIndex = Number.parseInt(slide.dataset.carouselLogicalIndex, 10);
            if (logicalIndex !== this.activeIndex) return;
            this.hoveredSlide = slide;
            this.isHovered = true;
            this.stopAutoplay();
          });
          slide.addEventListener('mouseleave', () => {
            if (this.hoveredSlide !== slide) return;
            this.hoveredSlide = null;
            this.isHovered = false;
            this.startAutoplay();
          });
        });
      }
      this.root.addEventListener('focusin', () => {
        this.hasFocus = true;
        this.stopAutoplay();
      });
      this.root.addEventListener('focusout', (event) => {
        if (!this.root.contains(event.relatedTarget)) {
          this.hasFocus = false;
          this.startAutoplay();
        }
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      });
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible) this.startAutoplay();
          else this.stopAutoplay();
        }, { threshold: 0.1 });
        this.visibilityObserver.observe(this.root);
      }
      this.update();
      this.startAutoplay();
    }

    setupInfiniteTrack() {
      if (this.originalSlides.length < 2) return;

      this.originalSlides.forEach((slide, index) => {
        slide.dataset.carouselLogicalIndex = index;
      });

      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();
      this.originalSlides.forEach((slide, index) => {
        before.append(this.createClone(slide, index));
        after.append(this.createClone(slide, index));
      });
      this.track.prepend(before);
      this.track.append(after);
      this.slides = [...this.track.querySelectorAll('.media-carousel__slide')];
      this.infinite = true;
      this.jumpToPhysicalIndex(this.originalSlides.length);
    }

    createClone(slide, logicalIndex) {
      const clone = slide.cloneNode(true);
      clone.classList.add('media-carousel__slide--clone');
      clone.dataset.carouselLogicalIndex = logicalIndex;
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
        element.removeAttribute('data-shopify-editor-block');
      });
      clone.querySelectorAll('[tabindex], a, button, input, select, textarea').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    startDrag(event) {
      if (event.button !== undefined && event.button !== 0) return;
      this.cancelScrollAnimation();
      this.stopAutoplay();
      this.drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startScrollLeft: this.track.scrollLeft,
        startIndex: this.closestSlideIndex(),
        moved: false,
      };
    }

    dragTrack(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      if (this.drag.vertical) return;
      const distance = event.clientX - this.drag.startX;
      const verticalDistance = event.clientY - this.drag.startY;
      if (!this.drag.moved && Math.abs(verticalDistance) > Math.abs(distance) && Math.abs(verticalDistance) >= 6) {
        this.drag.vertical = true;
        return;
      }
      if (!this.drag.moved && Math.abs(distance) < 6) return;
      if (!this.drag.moved) {
        this.drag.moved = true;
        this.track.classList.add('is-dragging');
        this.track.setPointerCapture?.(event.pointerId);
      }
      event.preventDefault();
      this.track.scrollLeft = this.drag.startScrollLeft - distance;
    }

    endDrag(event, cancelled = false) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      const drag = this.drag;
      const distance = event.clientX - drag.startX;
      this.drag = null;
      this.track.classList.remove('is-dragging');
      if (this.track.hasPointerCapture?.(event.pointerId)) this.track.releasePointerCapture(event.pointerId);

      if (drag.moved) {
        this.suppressClick = true;
        window.setTimeout(() => { this.suppressClick = false; }, 0);
        if (!cancelled) {
          const closestIndex = this.closestSlideIndex();
          const swipeThreshold = Math.min(50, this.track.clientWidth * 0.1);
          let target = closestIndex;
          if (Math.abs(distance) >= swipeThreshold && closestIndex === drag.startIndex) {
            target = drag.startIndex + (distance < 0 ? 1 : -1);
          }
          if (this.infinite) this.goToPhysical(target);
          else this.goTo(Math.min(Math.max(target, 0), this.lastStartIndex()));
        }
      }
      this.restartAutoplay();
    }

    closestSlideIndex() {
      if (!this.slides.length) return 0;
      return this.slides.reduce((closest, slide, index) => (
        Math.abs(slide.offsetLeft - this.track.offsetLeft - this.track.scrollLeft)
          < Math.abs(this.slides[closest].offsetLeft - this.track.offsetLeft - this.track.scrollLeft) ? index : closest
      ), 0);
    }

    move(direction) {
      if (this.infinite) {
        this.goToPhysical(this.closestSlideIndex() + direction);
        return;
      }
      const target = Math.min(Math.max(this.activeIndex + direction, 0), this.lastStartIndex());
      this.goTo(target);
    }

    goTo(index) {
      if (this.infinite) {
        const current = this.closestSlideIndex();
        const candidates = this.slides
          .map((slide, physicalIndex) => ({
            physicalIndex,
            logicalIndex: Number.parseInt(slide.dataset.carouselLogicalIndex, 10),
          }))
          .filter((candidate) => candidate.logicalIndex === index);
        const target = candidates.reduce((closest, candidate) => (
          Math.abs(candidate.physicalIndex - current) < Math.abs(closest.physicalIndex - current)
            ? candidate
            : closest
        ), candidates[0]);
        if (target) this.goToPhysical(target.physicalIndex);
        return;
      }
      this.goToPhysical(index);
    }

    goToPhysical(index) {
      const slide = this.slides[index];
      if (!slide) return;
      const target = slide.offsetLeft - this.track.offsetLeft;
      this.cancelScrollAnimation();
      this.isNavigating = true;
      const logicalIndex = Number.parseInt(slide.dataset.carouselLogicalIndex, 10);
      if (Number.isFinite(logicalIndex)) this.setActiveIndex(logicalIndex);
      if (this.animationStyle === 'momentum' && !this.reducedMotion) {
        this.animateMomentumScroll(target);
      } else {
        this.track.scrollTo({ left: target, behavior: this.reducedMotion ? 'auto' : 'smooth' });
        window.clearTimeout(this.navigationTimer);
        this.navigationTimer = window.setTimeout(() => this.finishNavigation(), this.reducedMotion ? 0 : 700);
      }
    }

    animateMomentumScroll(target) {
      const start = this.track.scrollLeft;
      const maximum = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      const destination = Math.min(Math.max(target, 0), maximum);
      const distance = destination - start;
      if (Math.abs(distance) < 1) {
        this.finishNavigation();
        return;
      }

      const duration = 1250;
      const startedAt = performance.now();
      this.track.classList.add('is-carousel-animating');

      const step = (now) => {
        const progress = Math.min((now - startedAt) / duration, 1);
        this.track.scrollLeft = start + (distance * this.momentumEase(progress));
        if (progress < 1) {
          this.animationFrame = window.requestAnimationFrame(step);
        } else {
          this.animationFrame = null;
          this.track.classList.remove('is-carousel-animating');
          this.finishNavigation();
        }
      };

      this.animationFrame = window.requestAnimationFrame(step);
    }

    momentumEase(progress) {
      const curve = (time, point1, point2) => {
        const inverse = 1 - time;
        return (3 * inverse * inverse * time * point1) + (3 * inverse * time * time * point2) + (time * time * time);
      };
      const slope = (time, point1, point2) => (
        (3 * (1 - time) * (1 - time) * point1)
        + (6 * (1 - time) * time * (point2 - point1))
        + (3 * time * time * (1 - point2))
      );
      let time = progress;
      for (let iteration = 0; iteration < 6; iteration += 1) {
        const currentSlope = slope(time, 0.16, 0.3);
        if (Math.abs(currentSlope) < 0.0001) break;
        time -= (curve(time, 0.16, 0.3) - progress) / currentSlope;
        time = Math.min(Math.max(time, 0), 1);
      }
      return curve(time, 1, 1);
    }

    cancelScrollAnimation() {
      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.track.classList.remove('is-carousel-animating');
    }

    jumpToPhysicalIndex(index) {
      const slide = this.slides[index];
      if (!slide) return;
      this.isNormalizing = true;
      const previousScrollBehavior = this.track.style.scrollBehavior;
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft = slide.offsetLeft - this.track.offsetLeft;
      this.track.style.scrollBehavior = previousScrollBehavior;
      this.isNormalizing = false;
    }

    scheduleScrollEnd() {
      if (this.isNormalizing) return;
      window.clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = window.setTimeout(() => this.finishNavigation(), 180);
    }

    finishNavigation() {
      if (this.drag || this.isNormalizing) return;
      window.clearTimeout(this.navigationTimer);
      window.clearTimeout(this.scrollEndTimer);
      this.navigationTimer = null;
      this.scrollEndTimer = null;
      this.isNavigating = false;
      this.update();
      this.normalizeInfiniteTrack();
    }

    normalizeInfiniteTrack() {
      if (!this.infinite || this.drag || this.isNavigating) return;
      const count = this.originalSlides.length;
      const physicalIndex = this.closestSlideIndex();
      if (physicalIndex < count) this.jumpToPhysicalIndex(physicalIndex + count);
      else if (physicalIndex >= count * 2) this.jumpToPhysicalIndex(physicalIndex - count);
    }

    startAutoplay(reset = false) {
      if (!this.autoplay || this.reducedMotion || this.lastStartIndex() < 1 || this.autoplayTimer || this.isHovered || this.hasFocus || !this.isVisible || document.hidden) return;
      if (reset) this.autoplayRemaining = this.autoplayDelay;
      this.root.classList.remove('is-autoplay-paused');
      if (reset || !this.root.classList.contains('is-autoplay-progressing')) this.restartProgress();
      this.autoplayStartedAt = performance.now();
      this.autoplayTimer = window.setTimeout(() => {
        this.autoplayTimer = null;
        this.autoplayStartedAt = null;
        this.autoplayRemaining = this.autoplayDelay;
        this.move(1);
        this.startAutoplay(true);
      }, this.autoplayRemaining);
    }

    stopAutoplay(reset = false) {
      if (this.autoplayTimer) {
        const elapsed = this.autoplayStartedAt === null ? 0 : performance.now() - this.autoplayStartedAt;
        this.autoplayRemaining = Math.max(this.autoplayRemaining - elapsed, 0);
        window.clearTimeout(this.autoplayTimer);
        this.autoplayTimer = null;
      }
      this.autoplayStartedAt = null;
      if (reset) this.autoplayRemaining = this.autoplayDelay;
      if (this.autoplay) this.root.classList.add('is-autoplay-paused');
    }

    restartAutoplay() {
      this.stopAutoplay(true);
      this.restartProgress();
      this.startAutoplay();
    }

    restartAutoplayAfterNavigation(event) {
      // Pointer activation can focus a control before its click handler runs,
      // which would otherwise leave autoplay and its progress indicator paused.
      // Keep the focus pause intact for keyboard-triggered clicks (detail === 0).
      if (event?.detail > 0 && event.currentTarget === document.activeElement) {
        event.currentTarget.blur();
      }
      this.restartAutoplay();
    }

    lastStartIndex() {
      if (this.infinite) return Math.max(this.originalSlides.length - 1, 0);
      return this.controlledSlides
        ? Math.max(this.slides.length - this.slidesVisible, 0)
        : Math.max(this.slides.length - 1, 0);
    }

    restartProgress() {
      this.root.classList.remove('is-autoplay-progressing');
      const progressIndicators = this.dots
        .map((dot) => dot.querySelector('.media-carousel__dot-progress'))
        .filter(Boolean);
      progressIndicators.forEach((indicator) => {
        indicator.style.animation = 'none';
      });
      void this.root.offsetWidth;
      progressIndicators.forEach((indicator) => {
        indicator.style.removeProperty('animation');
      });
      this.root.classList.add('is-autoplay-progressing');
    }

    setActiveIndex(index) {
      const normalizedIndex = this.infinite && this.originalSlides.length
        ? ((index % this.originalSlides.length) + this.originalSlides.length) % this.originalSlides.length
        : Math.min(Math.max(index, 0), this.lastStartIndex());
      const activeIndexChanged = normalizedIndex !== this.activeIndex;
      this.activeIndex = normalizedIndex;
      this.dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === normalizedIndex);
        if (dotIndex === normalizedIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (activeIndexChanged && !this.isNavigating && !this.drag) this.restartAutoplay();
    }

    update() {
      const max = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      if (this.previous) this.previous.disabled = !this.infinite && this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = !this.infinite && this.track.scrollLeft >= max - 2;
      if (!this.slides.length) return;
      if (this.isNavigating) return;
      let physicalIndex;
      if (this.controlledSlides) {
        physicalIndex = this.slides.reduce((closest, slide, index) => (
          Math.abs(slide.offsetLeft - this.track.offsetLeft - this.track.scrollLeft)
            < Math.abs(this.slides[closest].offsetLeft - this.track.offsetLeft - this.track.scrollLeft) ? index : closest
        ), 0);
      } else {
        const viewportCenter = this.track.scrollLeft + (this.track.clientWidth / 2);
        physicalIndex = this.slides.reduce((closest, slide, index) => (
          Math.abs((slide.offsetLeft - this.track.offsetLeft) + (slide.offsetWidth / 2) - viewportCenter)
            < Math.abs((this.slides[closest].offsetLeft - this.track.offsetLeft) + (this.slides[closest].offsetWidth / 2) - viewportCenter) ? index : closest
        ), 0);
      }
      const logicalIndex = this.infinite
        ? Number.parseInt(this.slides[physicalIndex].dataset.carouselLogicalIndex, 10)
        : Math.min(physicalIndex, this.lastStartIndex());
      if (Number.isFinite(logicalIndex)) this.setActiveIndex(logicalIndex);
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-media-carousel]').forEach((root) => new MediaCarousel(root).init());
    if (window.Trustpilot?.loadFromElement) {
      scope.querySelectorAll('.content-section .trustpilot-widget').forEach((widget) => {
        if (!widget.querySelector('iframe')) window.Trustpilot.loadFromElement(widget, true);
      });
    }
  };

  window.ReusableSections = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
